import React, { useContext } from 'react';
import PropTypes from 'prop-types';

import { Badge, Form, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import dayjs from 'dayjs';
import { faTrashCan } from 'images/icons/fsa-monotone-svg-icons';
import { faBan, faBuilding, faEdit } from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Swal from 'sweetalert2';
import Tooltip from 'react-bootstrap/Tooltip';

import { areYouSure } from 'lib/utils/toast';
import {
  backgroundGrey,
  coolGrey,
  grape,
  greyStroke,
  salmon,
  softAmber,
  white,
} from 'lib/css/colors';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import { BRANDS_DELETE } from 'lib/networking/endpoints';
import Container from 'components/shared/Container';
import CopyableText from 'components/shared/CopyableText';
import EmptyState from 'components/shared/EmptyState';
import { DATE_FORMAT_WITH_TIME } from 'lib/constants';
import { ManageBrandsContext } from 'components/manage_brands/ManageBrandsView';
import pointerOnHover from 'lib/css/pointerOnHover';
import PrivateLabelBrandBadge from 'components/manage_brands/brand_details/PrivateLabelBrandBadge';
import usePost from 'lib/hooks/usePost';
import VerificationStatusBadge from 'components/shared/VerificationStatusBadge';

function ManageBrandsTable({ brands, setBrandEditModalSettings, refetch }) {
  const { mergeList, setMergeList } = useContext(ManageBrandsContext);

  const { postData: deleteBrand } = usePost(
    BRANDS_DELETE,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Deleted successfully',
        title: 'Success',
        position: 'center',
      });
      refetch();
    },
    error => {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: `An error occurred: ${error?.response?.data?.message}`,
      });
    },
  );

  return (
    <Container>
      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            <th>
              <Form.Check
                type="checkbox"
                checked={brands?.length > 0 && mergeList?.length === brands?.length}
                onChange={e => {
                  if (e.target.checked) {
                    setMergeList([...brands]);
                  } else {
                    setMergeList([]);
                  }
                }}
              />
            </th>
            <th className={css(styles.logoHeader)}>Logo</th>
            <th>Brand Name</th>
            <th>GLN</th>
            <th>Manufacturer</th>
            <th># of Products</th>
            <th>Status</th>
            <th>Last Modified</th>
            <th>Created</th>
            <th />
            <th />
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {(!brands || brands.length === 0) && (
            <tr>
              <td colSpan={11}>
                <EmptyState
                  icon={faBuilding}
                  title="No brands found"
                  description={
                    'Try adjusting your search or filter criteria ' +
                    "to find what you're looking for."
                  }
                />
              </td>
            </tr>
          )}
          {brands?.map((brand, index) => (
            <tr
              key={index}
              className={css(styles.clickableRow)}
              onClick={() => window.open(`/enrich-products/brand-details/${brand.id}`, '_blank')}
            >
              <td onClick={e => e.stopPropagation()}>
                <Form.Check
                  type="checkbox"
                  checked={mergeList.some(mergeBrand => mergeBrand.id === brand.id)}
                  onChange={e => {
                    if (e.target.checked) {
                      if (!mergeList.some(mergeBrand => mergeBrand.id === brand.id)) {
                        setMergeList([...mergeList, brand]);
                      }
                    } else {
                      setMergeList(mergeList.filter(mergeBrand => mergeBrand.id !== brand.id));
                    }
                  }}
                />
              </td>
              <td className={css(styles.logoCell)}>
                <img
                  src={brand.logo_url || placeholderProductImage}
                  alt={`${brand.name} logo`}
                  className={css(styles.brandLogo)}
                  onError={e => {
                    e.target.onerror = null;
                    e.target.src = placeholderProductImage;
                  }}
                />
              </td>
              <td>
                <div className={css(styles.brandNameContainer)}>
                  <CopyableText text={brand.name} tooltipText="Copy name" />
                  <PrivateLabelBrandBadge brand={brand} />
                  {brand.is_retail && (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>This brand is a retail brand</Tooltip>}
                    >
                      <Badge variant="info" className={css(styles.retailBadge)}>
                        R
                      </Badge>
                    </OverlayTrigger>
                  )}
                  {brand.is_discontinued && (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>This brand is discontinued</Tooltip>}
                    >
                      <Badge variant="info" className={css(styles.discontinuedBadge)}>
                        <FontAwesomeIcon icon={faBan} />
                      </Badge>
                    </OverlayTrigger>
                  )}
                  {brand.source && (
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>{`Source is: ${brand.source}`}</Tooltip>}
                    >
                      <Badge variant="light" className={css(styles.sourceBadge)}>
                        {brand.source.charAt(0).toUpperCase()}
                      </Badge>
                    </OverlayTrigger>
                  )}
                </div>
              </td>
              <td>
                {brand.global_manufacturer?.manufacturer_glns &&
                Object.keys(brand.global_manufacturer.manufacturer_glns).length > 0 ? (
                  <CopyableText
                    text={Object.keys(brand.global_manufacturer.manufacturer_glns).join(', ')}
                    tooltipText="Copy GLN"
                  >
                    <div>
                      {Object.entries(brand.global_manufacturer.manufacturer_glns).map(
                        ([gln, subCompanyName], idx) => (
                          <div key={idx}>
                            {gln}
                            {subCompanyName ? ` (${subCompanyName})` : ''}
                          </div>
                        ),
                      )}
                    </div>
                  </CopyableText>
                ) : (
                  '-'
                )}
              </td>
              <td>
                {brand.global_manufacturer?.name ? (
                  <CopyableText
                    text={brand.global_manufacturer.name}
                    tooltipText="Copy manufacturer name"
                  />
                ) : (
                  '-'
                )}
              </td>
              <td>{brand.product_count}</td>
              <td>
                <VerificationStatusBadge status={brand.verification_status} />
              </td>
              <td>{dayjs(brand.date_modified).format(DATE_FORMAT_WITH_TIME)}</td>
              <td>{dayjs(brand.date_created).format(DATE_FORMAT_WITH_TIME)}</td>
              <td className={css(styles.actionCell)}>
                <OverlayTrigger overlay={<Tooltip>Edit brand</Tooltip>}>
                  <FontAwesomeIcon
                    icon={faEdit}
                    className={classNames(css(styles.editButton), pointerOnHover)}
                    onClick={e => {
                      e.stopPropagation();
                      setBrandEditModalSettings({ show: true, brand: brand });
                    }}
                  />
                </OverlayTrigger>
              </td>
              <td className={css(styles.actionCell)}>
                <OverlayTrigger overlay={<Tooltip>Delete brand</Tooltip>}>
                  <FontAwesomeIcon
                    icon={faTrashCan}
                    className={classNames(css(styles.deleteButton), pointerOnHover)}
                    onClick={e => {
                      e.stopPropagation();
                      areYouSure(
                        () => deleteBrand({ id: brand.id }),
                        'Are you sure you want to delete this brand?',
                      );
                    }}
                  />
                </OverlayTrigger>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
  actionCell: {
    textAlign: 'center',
    width: 40,
  },
  editButton: {
    color: grape,
    fontSize: 16,
    ':hover': {
      opacity: 0.7,
    },
  },
  deleteButton: {
    color: salmon,
    fontSize: 16,
    ':hover': {
      opacity: 0.7,
    },
  },
  brandNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  retailBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: grape,
    color: white,
    padding: '2px 6px',
    borderRadius: 4,
    cursor: 'help',
  },
  discontinuedBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: softAmber,
    color: white,
    padding: '2px 6px',
    borderRadius: 4,
    cursor: 'help',
  },
  sourceBadge: {
    fontSize: '0.7rem',
    fontWeight: 600,
    backgroundColor: coolGrey,
    color: white,
    padding: '2px 6px',
    borderRadius: 4,
    cursor: 'help',
  },
  logoHeader: {
    width: 70,
    textAlign: 'center',
  },
  logoCell: {
    width: 70,
    padding: '4px 8px',
  },
  brandLogo: {
    width: 48,
    height: 48,
    objectFit: 'contain',
    borderRadius: 6,
    border: `1px solid ${greyStroke}`,
    backgroundColor: '#fff',
  },
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: `${grape}10`,
    },
  },
});

ManageBrandsTable.propTypes = {
  brands: PropTypes.array,
  setBrandEditModalSettings: PropTypes.func,
  refetch: PropTypes.func,
};

export default ManageBrandsTable;
